import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import { Link } from 'react-router-dom'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import Paginator from '../../components/Table/Paginator'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { TH } from './conductorTableCells'
import {
  cadenceStepChip,
  displayAssignee,
  filterCadenceRows,
  formatConductorDate,
  isDueOverdue,
  paginatedListMode,
  shouldShowPaginator,
  tenantAccountPath,
} from './conductorUtils'
import { useClampedPage } from './useClampedPage'
import { useGetConductorCadenceQueueQuery, useGetConductorPortfolioQuery } from './conductorApi'
import { formatUTCDate } from '../../util/string'

import type { ConductorCadenceQueueRow } from './conductor.types'

const CADENCE_PAGE_SIZE = 100
const CADENCE_FETCH_SIZE = 500

const CADENCE_ROLES = ['billing-ops', 'collections'] as const

type CauseOption = {
  value: string
  label: string
  group?: string
  gateId?: string
  subShape?: string
}

const CAUSE_OPTIONS: CauseOption[] = [
  { value: '', label: 'All causes' },
  { value: 'utility_bill_current', label: 'All grid bill', group: 'Grid bill', gateId: 'utility_bill_current' },
  {
    value: 'utility_bill_current|grid_bill_overdue',
    label: 'Grid bill overdue',
    group: 'Grid bill',
    gateId: 'utility_bill_current',
    subShape: 'grid_bill_overdue',
  },
  {
    value: 'utility_bill_current|no_grid_bill_ever',
    label: 'Never received a grid bill',
    group: 'Grid bill',
    gateId: 'utility_bill_current',
    subShape: 'no_grid_bill_ever',
  },
  { value: 'tenant_invoice_current', label: 'All invoice', group: 'Invoice', gateId: 'tenant_invoice_current' },
  {
    value: 'tenant_invoice_current|not_invoiced_bill_available',
    label: 'Invoice lag',
    group: 'Invoice',
    gateId: 'tenant_invoice_current',
    subShape: 'not_invoiced_bill_available',
  },
  {
    value: 'tenant_invoice_current|not_invoiced_no_bill',
    label: 'Grid not far enough ahead',
    group: 'Invoice',
    gateId: 'tenant_invoice_current',
    subShape: 'not_invoiced_no_bill',
  },
  {
    value: 'tenant_invoice_current|never_invoiced',
    label: 'Never invoiced',
    group: 'Invoice',
    gateId: 'tenant_invoice_current',
    subShape: 'never_invoiced',
  },
  {
    value: 'tenant_invoice_current|invoice_overdue',
    label: 'KE invoice overdue',
    group: 'Invoice',
    gateId: 'tenant_invoice_current',
    subShape: 'invoice_overdue',
  },
  {
    value: 'tenant_invoice_current|no_ke_invoice',
    label: 'KE invoice never issued',
    group: 'Invoice',
    gateId: 'tenant_invoice_current',
    subShape: 'no_ke_invoice',
  },
  {
    value: 'billing_status_coherent',
    label: 'All billing status',
    group: 'Billing status',
    gateId: 'billing_status_coherent',
  },
  {
    value: 'billing_status_coherent|reason_no_longer_holds',
    label: 'Reasons do not match facts',
    group: 'Billing status',
    gateId: 'billing_status_coherent',
    subShape: 'reason_no_longer_holds',
  },
  {
    value: 'billing_status_coherent|stopped_without_reason',
    label: 'Stopped without a reason',
    group: 'Billing status',
    gateId: 'billing_status_coherent',
    subShape: 'stopped_without_reason',
  },
  {
    value: 'billing_status_coherent|missing_mechanical_reason',
    label: 'Missing mechanical reason',
    group: 'Billing status',
    gateId: 'billing_status_coherent',
    subShape: 'missing_mechanical_reason',
  },
  {
    value: 'account_worth_pursuing',
    label: 'All close or salvage',
    group: 'Close or salvage',
    gateId: 'account_worth_pursuing',
  },
  {
    value: 'account_worth_pursuing|chronically_unbilled',
    label: 'Chronically unbilled',
    group: 'Close or salvage',
    gateId: 'account_worth_pursuing',
    subShape: 'chronically_unbilled',
  },
]

const CAUSE_GROUPS = ['Grid bill', 'Invoice', 'Billing status', 'Close or salvage'] as const

const selectClasses =
  'rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

const causeFromValue = (value: string): CauseOption =>
  CAUSE_OPTIONS.find((opt) => opt.value === value) ?? CAUSE_OPTIONS[0]

/** Bill-cycle ends are UTC calendar days at midnight. Due stays a local SLA instant. */
const calendarDate = (iso: string | null): string => {
  if (iso == null || iso === '') {
    return '—'
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return formatUTCDate(date) || '—'
}

const linkedLabel = (row: ConductorCadenceQueueRow): string => {
  if (row.isLinked == null) {
    return '—'
  }
  if (!row.isLinked) {
    return 'No'
  }
  if (row.lastRunDaysAgo == null) {
    return 'Yes'
  }
  return `Yes (${row.lastRunDaysAgo}d)`
}

const AccountNumberCell: React.FC<{ row: ConductorCadenceQueueRow }> = ({ row }) => {
  const href = tenantAccountPath({ tenantCuid: row.tenantCuid, accountNumber: row.accountNumber })
  const number = row.accountNumber ?? row.subjectCuid
  if (href == null) {
    return <span className="font-mono text-sm text-slate-900 dark:text-slate-100">{number}</span>
  }
  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-sm text-indigo-600 hover:underline dark:text-indigo-400"
    >
      {number}
    </Link>
  )
}

const CadenceRow: React.FC<{ row: ConductorCadenceQueueRow }> = ({ row }) => {
  const overdue = isDueOverdue(row.dueAt)
  return (
    <tr className="border-t border-slate-100 dark:border-slate-700">
      <td className="min-w-[8.5rem] whitespace-nowrap px-3 py-2">
        <AccountNumberCell row={row} />
      </td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{row.accountName ?? '—'}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{row.why}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{calendarDate(row.gridBillEndDate)}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{calendarDate(row.kingBillEndDate)}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{linkedLabel(row)}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{row.utility ?? '—'}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {row.billingStatus != null && <Badge status={row.billingStatus} />}
          {row.reasons.map((reason) => (
            <Badge key={reason} status={reason} />
          ))}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {row.openSteps.map((step) => (
            <Badge key={step} status={step} displayLabel={cadenceStepChip(step)} />
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{row.ownerRole ?? '—'}</td>
      <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{displayAssignee(row.assigneeUserId)}</td>
      <td className="px-3 py-2 text-sm">
        {row.dueAt == null ? (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        ) : (
          <span
            className={classnames(
              overdue ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'
            )}
          >
            {formatConductorDate(row.dueAt)}
            {overdue && ' (overdue)'}
          </span>
        )}
      </td>
      <td className="w-8 px-2 py-2 text-sm">
        <Link
          to={`/conductor/instances/${row.workflowInstanceCuid}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open workflow"
          title="Open workflow"
          className="inline-flex text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </Link>
      </td>
    </tr>
  )
}

export const Cadence: React.FC = () => {
  const [causeValue, setCauseValue] = useState('')
  const [role, setRole] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const { data: portfolio } = useGetConductorPortfolioQuery()

  const setSearchFilter = (value: string): void => {
    setSearch(value)
    setPage(0)
  }

  const setCauseFilter = (value: string): void => {
    setCauseValue(value)
    setPage(0)
  }

  const setRoleFilter = (value: string): void => {
    setRole(value)
    setPage(0)
  }

  const setOverdueFilter = (value: boolean): void => {
    setOverdueOnly(value)
    setPage(0)
  }

  const cause = causeFromValue(causeValue)

  const queueQuery = useGetConductorCadenceQueueQuery({
    resultsPerPage: CADENCE_FETCH_SIZE,
    page: 1,
  })

  const queueData = queueQuery.data
  const listedRows = useMemo(() => filterCadenceRows(queueData?.results ?? [], {}), [queueData?.results])
  const filteredRows = useMemo(
    () =>
      filterCadenceRows(listedRows, {
        gateId: cause.gateId,
        subShape: cause.subShape,
        ownerRole: role === '' ? undefined : role,
        overdueOnly,
        search,
      }),
    [listedRows, cause.gateId, cause.subShape, role, overdueOnly, search]
  )
  const pagedRows = filteredRows.slice(page * CADENCE_PAGE_SIZE, page * CADENCE_PAGE_SIZE + CADENCE_PAGE_SIZE)
  useClampedPage(filteredRows.length, CADENCE_PAGE_SIZE, page, setPage)

  const roles = useMemo(() => {
    const fromPortfolio = portfolio?.openWorkItemsByRole.map((r) => r.ownerRole) ?? []
    const fromResults = listedRows.map((item) => item.ownerRole).filter((r): r is string => r != null)
    return [...new Set([...CADENCE_ROLES, ...fromPortfolio, ...fromResults])].sort((a, b) => a.localeCompare(b))
  }, [portfolio, listedRows])

  const listMode =
    queueData == null
      ? null
      : paginatedListMode({
          total: filteredRows.length,
          results: pagedRows,
          resultsPerPage: CADENCE_PAGE_SIZE,
        })

  return (
    <div className="space-y-4 p-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Cadence</h2>
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
          Accounts with open billing-cadence-triage work. Why comes from the upstream FAIL gate, not billing status.
        </p>
        <ConductorFieldKey screen="cadence" />
      </div>

      <Panel header="Accounts">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Cause
            <select
              aria-label="Filter by cause"
              value={causeValue}
              onChange={(e) => setCauseFilter(e.target.value)}
              className={selectClasses}
            >
              <option value="">All causes</option>
              {CAUSE_GROUPS.map((group) => (
                <optgroup key={group} label={group}>
                  {CAUSE_OPTIONS.filter((opt) => opt.group === group).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Owner
            <select
              aria-label="Filter by owner"
              value={role}
              onChange={(e) => setRoleFilter(e.target.value)}
              className={selectClasses}
            >
              <option value="">All owners</option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            <input
              type="checkbox"
              aria-label="Past due date"
              checked={overdueOnly}
              onChange={(e) => setOverdueFilter(e.target.checked)}
            />
            Past due date
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            Account
            <input
              type="search"
              aria-label="Search account number or name"
              value={search}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="number or name"
              className={selectClasses}
            />
          </label>
        </div>

        {queueData != null && queueData.total > queueData.results.length && (
          <p role="status" className="mb-3 text-xs text-amber-800 dark:text-amber-200">
            Showing {queueData.results.length} of {queueData.total} accounts. Search and filters only cover the first{' '}
            {queueData.results.length}.
          </p>
        )}

        {queueQuery.isError ? (
          <QueryError message="Failed to load cadence queue." onRetry={() => void queueQuery.refetch()} />
        ) : queueData == null ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : listMode === 'empty' ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            No cadence accounts match the current filters.
          </div>
        ) : (
          <>
            {listMode === 'page-pending' && (
              <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading page…</div>
            )}
            {listMode === 'content' && (
              <div className="max-h-[70vh] overflow-auto">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(148,163,184,0.6)] dark:bg-slate-800 dark:shadow-[0_1px_0_0_rgba(71,85,105,0.8)]">
                    <tr>
                      <TH>Account</TH>
                      <TH>Name</TH>
                      <TH>Why</TH>
                      <TH>Grid</TH>
                      <TH>KE invoice</TH>
                      <TH>Linked</TH>
                      <TH>Utility</TH>
                      <TH>Billing status</TH>
                      <TH>Open steps</TH>
                      <TH>Owner</TH>
                      <TH>Assignee</TH>
                      <TH>Due</TH>
                      <TH>
                        <span className="sr-only">Workflow</span>
                      </TH>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((item) => (
                      <CadenceRow key={item.subjectCuid} row={item} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {shouldShowPaginator(filteredRows.length, CADENCE_PAGE_SIZE) && (
              <Paginator
                className="-mx-4 mt-2 border-t border-slate-200 dark:border-slate-700"
                data={{
                  total: filteredRows.length,
                  resultsPerPage: CADENCE_PAGE_SIZE,
                  page,
                }}
                handlePaginatorClick={setPage}
              />
            )}
          </>
        )}
      </Panel>
    </div>
  )
}

export default Cadence
